import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

// Tạo axios instance với base config
const api = axios.create({
  baseURL: `${API_URL}/notifications`,
  timeout: 10000,
});

// Interceptor để thêm token vào headers
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.userId) {
      config.headers.Authorization = `Bearer ${user.userId}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor để xử lý response
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error('Notification API Error:', error);
    return Promise.reject(error.response?.data || error);
  }
);

// ==================== USER FUNCTIONS ====================

/**
 * Lấy thông báo cho user
 * @param {string} page - Trang hiện tại (dashboard, scheduler, history, reports, all)
 * @param {boolean} includeRead - Có bao gồm thông báo đã đọc không
 */
export const getUserNotifications = async (page = null, includeRead = false) => {
  try {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (includeRead) params.append('includeRead', 'true');
    
    const response = await api.get(`/?${params.toString()}`);
    return {
      success: true,
      data: response.data,
      unreadCount: response.unreadCount
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Đánh dấu thông báo đã đọc
 * @param {string} notificationId - ID của thông báo
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await api.put(`/${notificationId}/read`);
    return {
      success: true,
      message: response.message
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Ghi nhận click action trên thông báo
 * @param {string} notificationId - ID của thông báo
 */
export const trackNotificationClick = async (notificationId) => {
  try {
    const response = await api.put(`/${notificationId}/click`);
    return {
      success: true,
      message: response.message
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

// ==================== ADMIN FUNCTIONS ====================

/**
 * Tạo thông báo mới (Admin only)
 * @param {Object} notificationData - Dữ liệu thông báo
 */
export const createNotification = async (notificationData) => {
  try {
    const response = await api.post('/', notificationData);
    return {
      success: true,
      data: response.data,
      message: response.message
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Lấy danh sách thông báo cho admin
 * @param {Object} filters - Bộ lọc và phân trang
 */
export const getAdminNotifications = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    
    const response = await api.get(`/admin?${params.toString()}`);
    return {
      success: true,
      data: response.data,
      pagination: response.pagination
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Cập nhật thông báo (Admin only)
 * @param {string} notificationId - ID của thông báo
 * @param {Object} updateData - Dữ liệu cập nhật
 */
export const updateNotification = async (notificationId, updateData) => {
  try {
    const response = await api.put(`/${notificationId}`, updateData);
    return {
      success: true,
      data: response.data,
      message: response.message
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Xóa thông báo (Admin only)
 * @param {string} notificationId - ID của thông báo
 */
export const deleteNotification = async (notificationId) => {
  try {
    const response = await api.delete(`/${notificationId}`);
    return {
      success: true,
      message: response.message
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Lấy thống kê thông báo (Admin only)
 */
export const getNotificationStats = async () => {
  try {
    const response = await api.get('/admin/stats');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Lấy danh sách users để chọn khi tạo thông báo specific
 */
export const getUsers = async () => {
  try {
    // Sử dụng auth API để lấy danh sách users
    const response = await axios.get(`${API_URL}/auth/users`, {
      headers: {
        Authorization: `Bearer ${JSON.parse(localStorage.getItem('user') || '{}').userId}`
      }
    });
    
    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    return {
      success: false,
      error: error.response?.data || error
    };
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Format thời gian còn lại của thông báo
 * @param {number} timeLeftMs - Thời gian còn lại tính bằng milliseconds
 */
export const formatTimeLeft = (timeLeftMs) => {
  if (timeLeftMs <= 0) return 'Đã hết hạn';
  
  const hours = Math.floor(timeLeftMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `Còn ${days} ngày`;
  } else if (hours > 0) {
    return `Còn ${hours} giờ`;
  } else {
    const minutes = Math.floor(timeLeftMs / (1000 * 60));
    return `Còn ${minutes} phút`;
  }
};

/**
 * Lấy màu sắc theo loại thông báo
 * @param {string} type - Loại thông báo
 */
export const getNotificationColor = (type) => {
  const colors = {
    info: '#2196f3',
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    announcement: '#9c27b0'
  };
  return colors[type] || colors.info;
};

/**
 * Lấy icon theo loại thông báo
 * @param {string} type - Loại thông báo
 */
export const getNotificationIcon = (type) => {
  const icons = {
    info: '📢',
    success: '✅',
    warning: '⚠️',
    error: '❌',
    announcement: '📣'
  };
  return icons[type] || icons.info;
};

/**
 * Lấy màu sắc theo độ ưu tiên
 * @param {string} priority - Độ ưu tiên
 */
export const getPriorityColor = (priority) => {
  const colors = {
    low: '#9e9e9e',
    medium: '#2196f3',
    high: '#ff9800',
    urgent: '#f44336'
  };
  return colors[priority] || colors.medium;
};

export default {
  getUserNotifications,
  markNotificationAsRead,
  trackNotificationClick,
  createNotification,
  getAdminNotifications,
  updateNotification,
  deleteNotification,
  getNotificationStats,
  getUsers,
  formatTimeLeft,
  getNotificationColor,
  getNotificationIcon,
  getPriorityColor
}; 